import { Code, ConnectError } from '@connectrpc/connect';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useProjectVolumes } from '../../../../hooks/useProjectVolumes';
import { projectDataClient, proposalReviewClient } from '../../../../lib/connect/clients';
import { flattenSections } from '../volume/workingOutlineHooks';
export const reviewKeys = {
    list: (projectId)=>[
            'proposalReviews',
            projectId,
            'list'
        ],
    detail: (projectId, reviewId)=>[
            'proposalReviews',
            projectId,
            'detail',
            reviewId
        ],
    all: (projectId)=>[
            'proposalReviews',
            projectId
        ],
    states: (projectId)=>[
            'sectionReviewStates',
            projectId
        ],
    sectionState: (projectId, sectionId)=>[
            'sectionReviewStates',
            projectId,
            sectionId
        ]
};
/** The server's message for a failed review call, shown inline as-is. */ export function reviewErrorMessage(err) {
    const e = ConnectError.from(err);
    if (e.code === Code.PermissionDenied) return "You don't have permission to do that.";
    return e.rawMessage || 'Something went wrong. Nothing was changed.';
}
export function useReviewList(projectId) {
    return useQuery({
        queryKey: reviewKeys.list(projectId),
        queryFn: async ()=>(await proposalReviewClient.listProposalReviews({
                projectId
            })).reviews,
        enabled: !!projectId
    });
}
export function useReviewDetail(projectId, reviewId) {
    return useQuery({
        queryKey: reviewKeys.detail(projectId, reviewId),
        queryFn: ()=>proposalReviewClient.getProposalReview({
                projectId,
                reviewId
            }),
        enabled: !!projectId && !!reviewId
    });
}
export function useSectionReviewState(projectId, sectionId) {
    return useQuery({
        queryKey: reviewKeys.sectionState(projectId, sectionId),
        queryFn: async ()=>{
            const resp = await proposalReviewClient.getSectionReviewStates({
                projectId,
                sectionIds: [
                    sectionId
                ]
            });
            return resp.states.find((s)=>s.sectionId === sectionId);
        },
        enabled: !!projectId && !!sectionId
    });
}
export function useAllSectionReviewStates(projectId, sectionIds) {
    return useQuery({
        queryKey: [
            ...reviewKeys.states(projectId),
            'all',
            sectionIds.join(',')
        ],
        queryFn: async ()=>{
            const resp = await proposalReviewClient.getSectionReviewStates({
                projectId,
                sectionIds
            });
            return new Map(resp.states.map((s)=>[
                    s.sectionId,
                    s
                ]));
        },
        enabled: !!projectId && sectionIds.length > 0
    });
}
/** Every section in the proposal across its volumes (shares the outline cache). */ export function useReviewSections(projectId) {
    const volumes = useProjectVolumes(projectId).data ?? [];
    const results = useQueries({
        queries: volumes.map((v)=>({
                queryKey: [
                    'outline-sections',
                    projectId,
                    v.volumeId
                ],
                queryFn: async ()=>(await projectDataClient.getSectionMap({
                        projectId,
                        volumeId: v.volumeId
                    })).sections,
                enabled: !!projectId
            }))
    });
    const sections = volumes.flatMap((v, i)=>flattenSections(results[i]?.data ?? []).map((s)=>({
                sectionId: s.sectionId,
                title: [
                    s.sectionNumber,
                    s.title
                ].filter(Boolean).join(' '),
                volumeId: v.volumeId,
                volumeName: v.displayName || v.originalIdentifier
            })));
    return {
        sections,
        isLoading: results.some((r)=>r.isLoading)
    };
}
